export type UserSettings = {
  user_id: number | undefined; // Only undefined during app-init/not logged in
  theme: string;
  language: string;
  class_name: string;
  widgets:
    | {
        id: number;
        translation_key?: string;
        order: number;
        x: number;
        y: number | null;
        w: number;
        h: number;
        filters?: Record<string, any>;
      }[]
    | null;
};

function detectLanguage(): string {
  const languages = navigator.languages?.length
    ? navigator.languages
    : [navigator.language ?? 'en-US'];
  for (const lang of languages) {
    if (lang.toLowerCase().includes('nl')) return 'nl-NL';
    if (lang.toLowerCase().includes('be')) return 'nl-NL';
    if (lang.toLowerCase().includes('en')) return 'en-US';
  }
  return 'en-US';
}

function userSettingsDefault(): UserSettings {
  return {
    language: detectLanguage(),
    theme: 'light-only',
    user_id: undefined,
    class_name: 'UserSettings',
    widgets: null,
  };
}

export const useUserSettingsStore = defineStore('userSettings', () => {
  const {setItem, getItem} = useLocalStorage();

  const userSettings = reactive(getItem<UserSettings>('userSettings') || userSettingsDefault());
  const loaded = ref(userSettings.user_id !== undefined);

  const apply = () => {
    setItem('userSettings', userSettings);
    document.body.className = userSettings.theme;
    document.documentElement.lang = userSettings.language.substring(0, 2); // en / nl
  };

  const replace = (newUserSettings: UserSettings) => {
    Object.assign(userSettings, newUserSettings);
    loaded.value = true;
    apply();
  };

  const setTheme = (theme: string) => {
    Object.assign(userSettings, {theme});
    apply();
  };

  const setLanguage = (language: string) => {
    Object.assign(userSettings, {language});
    apply();
  };

  const setWidgets = (widgets: UserSettings['widgets']) => {
    Object.assign(userSettings, {widgets});
    apply();
  };

  /** Retrieve the user settings again from the backend */
  const forceRefresh = async () => {
    const {data} = await apiGetUserSettings();
    replace(data);
  };

  const save = async () => {
    const response = await apiPutUserSettings(userSettings);
    const data: UserSettings = response.data;
    Object.assign(userSettings, data);
    apply();
  };

  return {
    userSettings,
    loaded,
    replace,
    setTheme,
    setLanguage,
    setWidgets,
    forceRefresh,
    save,
  };
});
